import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Trophy, Crown, Medal, Users, Search, ArrowLeft, Star, Award, TrendingUp, Mic, X as XIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const COMP_META = {
  campionato: { label: "Campionato", emoji: "🏆", color: "from-amber-500 to-yellow-600" },
  coppa_di_lega: { label: "Coppa di Lega", emoji: "🥇", color: "from-purple-500 to-fuchsia-600" },
  champions_league: { label: "Champions League", emoji: "🌟", color: "from-blue-500 to-cyan-500" },
  supercoppa: { label: "Supercoppa", emoji: "👑", color: "from-rose-500 to-red-600" },
};
const MEDALS = ["🥇", "🥈", "🥉"];

export default function StoricoMagnati() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "albo";
  const [tab, setTab] = useState(initialTab);

  // Mantieni il tab attivo sincronizzato con il query param ?tab=... così che:
  //  1) Se navighi a un giocatore e torni indietro, il tab si ripristina
  //  2) Il link "/storico-magnati?tab=players" apre direttamente Giocatori
  const changeTab = (t) => {
    setTab(t);
    // replace evita di inflare la history: cambiare tab non è "avanti"
    setSearchParams({ tab: t }, { replace: true });
  };
  const [albo, setAlbo] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [players, setPlayers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [podcast, setPodcast] = useState(null);
  const [podcastLoading, setPodcastLoading] = useState(false);
  const [leagueName, setLeagueName] = useState("");

  async function loadPodcast(seasonNum, regenerate = false) {
    setPodcastLoading(true);
    setPodcast({ season_num: seasonNum, loading: true });
    try {
      const r = regenerate
        ? await api.post(`/magnati/podcast/${seasonNum}/regenerate`)
        : await api.get(`/magnati/podcast/${seasonNum}`);
      setPodcast(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setPodcast(null);
    } finally {
      setPodcastLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const [a, c, lg] = await Promise.all([
          api.get("/magnati/albo-doro"),
          api.get("/magnati/clubs"),
          api.get("/magnati/lega").catch(() => ({ data: null })),
        ]);
        setAlbo(a.data.seasons || []);
        setClubs(c.data.clubs || []);
        setLeagueName(lg.data?.name || "");
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  // Search players (debounce trivially)
  useEffect(() => {
    if (!search.trim()) { setPlayers([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get(`/magnati/rooster?q=${encodeURIComponent(search.trim())}&limit=20`);
        setPlayers(r.data.players || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const trophiesRanking = useMemo(() => {
    const arr = clubs.map(c => {
      const total = Object.values(c.palmares || {}).reduce((s, x) => s + (x?.length || 0), 0);
      return { ...c, total_trophies: total };
    }).sort((a, b) => b.total_trophies - a.total_trophies);
    return arr;
  }, [clubs]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="font-mono tracking-[0.4em] text-slate-500 uppercase text-sm">Caricamento storico…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      {/* Header */}
      <header className="border-b border-amber-500/20 bg-slate-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              data-testid="storico-back-btn"
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-amber-500/70">MyFantabusiness · Archivio</div>
              <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500">
                Storico {leagueName || "Lega"}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 hidden sm:flex" data-testid="storico-seasons-badge">
              {albo.length} stagioni chiuse
            </Badge>
            <Link to="/storico-magnati/rosa-sinottica" data-testid="link-rosa-sinottica"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 text-xs font-mono uppercase tracking-widest transition-colors">
              🎯 Rosa Sinottica 26/27
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={tab} onValueChange={changeTab} className="w-full">
          <TabsList className="grid grid-cols-4 max-w-2xl mx-auto mb-8 bg-slate-900 border border-slate-800" data-testid="storico-tabs">
            <TabsTrigger value="albo" data-testid="tab-albo">🏆 Albo d&apos;Oro</TabsTrigger>
            <TabsTrigger value="clubs" data-testid="tab-clubs">🏟️ Club</TabsTrigger>
            <TabsTrigger value="trophies" data-testid="tab-trophies">🥇 Trofei</TabsTrigger>
            <TabsTrigger value="players" data-testid="tab-players">👤 Giocatori</TabsTrigger>
          </TabsList>

          {/* --- ALBO D'ORO --- */}
          <TabsContent value="albo" className="space-y-8">
            {albo.map(season => (
              <Card key={season.season_num} className="bg-slate-900/60 border-slate-800" data-testid={`albo-season-${season.season_num}`}>
                <CardHeader className="border-b border-slate-800">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      <span className="font-mono text-amber-400">Stagione {season.season_label}</span>
                    </CardTitle>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => loadPodcast(season.season_num)}
                        data-testid={`podcast-btn-${season.season_num}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500/20 border border-rose-500/40 text-rose-300 hover:bg-rose-500/30 text-xs font-mono uppercase tracking-widest transition-colors"
                        title="Genera podcast AI di questa stagione">
                        <Mic className="w-3.5 h-3.5" /> Podcast AI
                      </button>
                      <div className="text-xs font-mono uppercase tracking-widest text-slate-500">
                        #{String(season.season_num).padStart(2, "0")}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                  {Object.entries(season.competitions || {}).map(([key, comp]) => {
                    const meta = COMP_META[key] || { label: comp.name, emoji: "🏆", color: "from-slate-600 to-slate-700" };
                    // Supercoppa has single winner shape
                    if (comp.winner_club_id) {
                      return (
                        <div key={key} className={`p-4 rounded-xl bg-gradient-to-br ${meta.color} shadow-lg`}>
                          <div className="text-2xl mb-1">{meta.emoji}</div>
                          <div className="text-xs font-mono uppercase tracking-widest text-white/70 mb-2">{meta.label}</div>
                          <div className="text-lg font-bold text-white">{comp.winner_club_name}</div>
                          {comp.manager_at_time && (
                            <div className="text-xs text-white/80 mt-1">mgr: {comp.manager_at_time}</div>
                          )}
                          {comp.note && (
                            <div className="text-[10px] text-white/60 italic mt-2">{comp.note}</div>
                          )}
                        </div>
                      );
                    }
                    // Podium shape
                    return (
                      <div key={key} className="p-4 rounded-xl bg-slate-950/50 border border-slate-800">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xl">{meta.emoji}</span>
                          <span className="text-xs font-mono uppercase tracking-widest text-slate-400">{meta.label}</span>
                        </div>
                        {(comp.podium || []).length === 0 ? (
                          <div className="text-slate-600 text-sm">—</div>
                        ) : (
                          <div className="space-y-1.5">
                            {(comp.podium || []).map((row, i) => (
                              <Link key={row.club_id} to={`/storico-magnati/club/${row.club_id}`}
                                className="flex items-center justify-between hover:bg-slate-800/50 rounded px-2 py-1 -mx-2 transition-colors">
                                <span className="flex items-center gap-2">
                                  <span className="text-lg">{MEDALS[i]}</span>
                                  <span className={i === 0 ? "font-bold text-amber-300" : "text-slate-300"}>{row.current_club_name || row.club_name_at_time}</span>
                                </span>
                                <span className="text-xs font-mono text-slate-500">{row.ranking_pts}pt</span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
                {(season.individual_awards || []).length > 0 && (
                  <div className="px-6 pb-6">
                    <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Premi Individuali</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                      {season.individual_awards.map((aw, idx) => (
                        <div key={idx} className="px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-800">
                          <div className="text-[10px] font-mono uppercase tracking-wider text-amber-500/80">{aw.award}</div>
                          <div className="text-sm font-semibold text-slate-200 mt-0.5">
                            {aw.players.map(p => p.name).join(" & ")}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {aw.players.map(p => p.club_name_at_time).filter(Boolean).join(" · ")}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </TabsContent>

          {/* --- CLUB --- */}
          <TabsContent value="clubs" className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {clubs.map(club => {
              const totalTrophies = Object.values(club.palmares || {}).reduce((s, x) => s + (x?.length || 0), 0);
              return (
                <Link key={club.id} to={`/storico-magnati/club/${club.id}`} data-testid={`club-card-${club.slug}`}>
                  <Card className="bg-slate-900/60 border-slate-800 hover:border-amber-500/40 transition-all hover:shadow-lg hover:shadow-amber-500/10 h-full">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="text-lg font-bold text-slate-100">{club.current_name}</div>
                          <div className="text-xs text-slate-500 mt-0.5">👤 {club.manager_name}</div>
                        </div>
                        {totalTrophies > 0 && (
                          <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
                            <Trophy className="w-3 h-3 mr-1" /> {totalTrophies}
                          </Badge>
                        )}
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-600 mb-2">Storia nomi</div>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {(club.name_history || []).slice(0, 4).map((h, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-400">
                            <span className="text-slate-600 mr-1">{h.season}</span>{h.name}
                          </span>
                        ))}
                      </div>
                      {totalTrophies > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {Object.entries(club.palmares || {}).map(([k, arr]) =>
                            arr?.length ? (
                              <div key={k} className="text-xs">
                                <span className="mr-1">{COMP_META[k]?.emoji}</span>
                                <span className="text-slate-400">×{arr.length}</span>
                              </div>
                            ) : null
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </TabsContent>

          {/* --- TROFEI (Ranking club per trofei totali) --- */}
          <TabsContent value="trophies">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-400" /> Classifica Trofei All-Time
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {trophiesRanking.map((c, i) => (
                  <Link key={c.id} to={`/storico-magnati/club/${c.id}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-950/50 border border-slate-800 hover:border-amber-500/40 transition-colors">
                    <div className="flex items-center gap-4">
                      <span className={`text-2xl font-mono font-bold w-10 text-center ${i === 0 ? "text-amber-400" : i < 3 ? "text-slate-300" : "text-slate-600"}`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <div className="font-bold text-slate-100">{c.current_name}</div>
                        <div className="text-xs text-slate-500">{c.manager_name}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        {Object.entries(c.palmares || {}).flatMap(([key, seasons]) =>
                          (seasons || []).map((s, si) => (
                            <div key={`${key}-${si}`} title={`${COMP_META[key]?.label} ${s}`} className="text-lg">
                              {COMP_META[key]?.emoji || "🏆"}
                            </div>
                          ))
                        )}
                      </div>
                      <Badge className="bg-amber-500 text-slate-950 font-bold ml-2">{c.total_trophies}</Badge>
                    </div>
                  </Link>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* --- PLAYERS SEARCH --- */}
          <TabsContent value="players">
            <div className="max-w-2xl mx-auto space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Cerca giocatore per nome…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="storico-search-input"
                  className="pl-10 bg-slate-900 border-slate-800 text-slate-100"
                />
              </div>
              {searching && <div className="text-center text-slate-500 text-sm">Cerca…</div>}
              {!searching && search && players.length === 0 && (
                <div className="text-center text-slate-500 py-8">Nessun giocatore trovato</div>
              )}
              <div className="grid gap-2">
                {players.map(p => (
                  <Link key={p.id} to={`/storico-magnati/giocatore/${p.id}`} data-testid={`player-row-${p.id}`}
                    className="p-3 rounded-lg bg-slate-900/60 border border-slate-800 hover:border-amber-500/40 transition-colors flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-slate-100">{p.canonical_name}</div>
                      <div className="text-xs text-slate-500">
                        {p.current_ruolo} · {p.current_squadra_a}
                        {(p.personal_awards || []).length > 0 && (
                          <span className="ml-2 text-amber-400">
                            <Award className="w-3 h-3 inline" /> {p.personal_awards.length}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(p.aliases || []).slice(0, 2).map((a, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-500">{a}</span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Podcast Modal */}
      {podcast && (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPodcast(null)}>
          <div className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl bg-gradient-to-br from-rose-900/40 via-slate-900 to-slate-950 border border-rose-500/40 shadow-2xl shadow-rose-500/20" onClick={e => e.stopPropagation()} data-testid="podcast-modal">
            <div className="sticky top-0 flex items-center justify-between p-5 border-b border-rose-500/30 bg-slate-950/60 backdrop-blur">
              <div className="flex items-center gap-3">
                <Mic className="w-6 h-6 text-rose-400" />
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-rose-400/80">Podcast Episode · Claude Sonnet 5</div>
                  <div className="text-xl font-bold text-rose-200">Stagione {podcast.season_label || podcast.season_num}</div>
                </div>
              </div>
              <button onClick={() => setPodcast(null)} data-testid="podcast-close-btn"
                className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {podcastLoading || podcast.loading ? (
                <div className="flex flex-col items-center gap-3 py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-rose-400 border-t-transparent"></div>
                  <div className="text-sm text-rose-300 font-mono uppercase tracking-widest">Sto raccontando la stagione…</div>
                </div>
              ) : (
                <>
                  <div className="prose prose-invert prose-amber max-w-none whitespace-pre-wrap text-slate-200 leading-relaxed">
                    {podcast.narrative}
                  </div>
                  <div className="mt-6 pt-4 border-t border-slate-800 flex items-center justify-between text-xs text-slate-500">
                    <span>Generato: {podcast.generated_at ? new Date(podcast.generated_at).toLocaleString("it-IT") : "—"}</span>
                    <button
                      onClick={() => loadPodcast(podcast.season_num, true)}
                      data-testid="podcast-regenerate-btn"
                      className="px-3 py-1 rounded bg-rose-500/20 border border-rose-500/40 text-rose-300 hover:bg-rose-500/30 font-mono uppercase tracking-widest">
                      Rigenera
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
